import { Link } from 'react-router'
import { useState } from 'react'
import { ChevronRight } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertDialog,
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  InfoBox,
  Input,
  LoadingState,
  Pagination,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Toast,
} from '@biznomi/ui-react'
import {
  getEmployees,
  getDepartments,
  issueTempPassword,
  terminateEmployee,
  restoreEmployee,
  type EmployeeRow,
} from '@/lib/api/admin'
import { useAuth } from '@/lib/auth/store'
import { useOrg } from '@/lib/useOrg'
import { EmployeeCreateDialog } from './EmployeeCreateDialog'
import { EmployeeBulkDialog } from './EmployeeBulkDialog'

const ALL = '__all__'
const PAGE_SIZE = 30

const ROLE_LABEL: Record<EmployeeRow['role'], string> = {
  // DCMC 요청(2026-07-31) 용어 — 앱 전체에서 이 표기로 통일
  ADMIN: '최종관리자',
  MANAGER: '관리자',
  EMPLOYEE: '직원',
}

type StatusFilter = 'active' | 'inactive' | 'all'

export default function Employees() {
  const me = useAuth((s) => s.user)
  const isAdmin = me?.role === 'ADMIN'
  const qc = useQueryClient()
  const { data: org } = useOrg()

  const [search, setSearch] = useState('')
  const [dept, setDept] = useState(ALL)
  const [status, setStatus] = useState<StatusFilter>('active')
  const [page, setPage] = useState(1)
  const [tempPassword, setTempPassword] = useState<{ name: string; password: string } | null>(null)

  const [createOpen, setCreateOpen] = useState(false)
  const [bulkOpen, setBulkOpen] = useState(false)
  // 퇴사·복구는 되돌리는 데 손이 가므로 한 번 되묻는다
  const [confirmLeave, setConfirmLeave] = useState<EmployeeRow | null>(null)
  const [confirmBack, setConfirmBack] = useState<EmployeeRow | null>(null)
  const [toast, setToast] = useState('')
  const [toastError, setToastError] = useState(false)

  function notify(message: string, isError = false) {
    setToastError(isError)
    setToast(message)
  }

  const { data: departments } = useQuery({
    queryKey: ['admin', 'departments'],
    queryFn: async () => {
      const res = await getDepartments()
      return res.success ? (res.data ?? []) : []
    },
    staleTime: 5 * 60 * 1000,
  })

  // 검색·부서·상태는 전부 서버가 거른다 — 화면에서 다시 거르지 않는다
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'employees', search, dept, status, page],
    queryFn: async () => {
      const res = await getEmployees({
        search: search.trim() || undefined,
        departmentId: dept === ALL ? undefined : dept,
        status,
        take: PAGE_SIZE,
        skip: (page - 1) * PAGE_SIZE,
      })
      if (!res.success || !res.data) throw new Error(res.error ?? '직원 목록을 불러오지 못했습니다')
      return res.data
    },
  })

  const issue = useMutation({
    mutationFn: (emp: EmployeeRow) => issueTempPassword(emp.id).then((r) => ({ r, emp })),
    onSuccess: ({ r, emp }) => {
      if (!r.success || !r.data) return
      setTempPassword({ name: emp.name, password: r.data.tempPassword })
    },
  })

  /**
   * 퇴사·복구.
   *
   * 마지막 활성 최종관리자를 퇴사시키면 아무도 조직을 관리할 수 없게 된다.
   * 서버가 그 경우만 403 으로 막으므로(lockoutGuard), 여기서는 서버 문구를 그대로 띄운다 —
   * 화면이 같은 판단을 따로 하면 두 규칙이 어긋나는 날이 온다.
   */
  const leave = useMutation({
    mutationFn: (e: EmployeeRow) => terminateEmployee(e.id),
    // ⚠️ ConfirmDialog 는 스스로 닫지 않는다. 성공이든 거절이든 여기서 닫아야 한다
    onSuccess: (res) => {
      setConfirmLeave(null)
      if (!res.success) return notify(res.error ?? '퇴사 처리에 실패했습니다', true)
      qc.invalidateQueries({ queryKey: ['admin', 'employees'] })
      notify(res.data?.message ?? '퇴사 처리했습니다')
    },
  })

  const back = useMutation({
    mutationFn: (e: EmployeeRow) => restoreEmployee(e.id),
    onSuccess: (res) => {
      setConfirmBack(null)
      if (!res.success) return notify(res.error ?? '복구에 실패했습니다', true)
      qc.invalidateQueries({ queryKey: ['admin', 'employees'] })
      notify(res.data?.message ?? '복구했습니다')
    },
  })

  function resetTo(fn: () => void) {
    fn()
    setPage(1)
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-bold">직원</h1>
        <div className="flex items-center gap-3">
          {data && (
            <p className="text-sm text-slate-500">
              재직 {data.activeCount}명 · 퇴사 {data.inactiveCount}명
            </p>
          )}
          {/* 등록·일괄등록은 ADMIN 만 — 서버도 403 이다 */}
          {isAdmin && (
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" onClick={() => setBulkOpen(true)}>
                엑셀로 여러 명
              </Button>
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                직원 추가
              </Button>
            </div>
          )}
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={search}
          onChange={(e) => resetTo(() => setSearch(e.target.value))}
          placeholder="이름·사번·이메일"
          className="h-9 w-48 text-sm"
        />

        {(departments?.length ?? 0) > 0 && (
          <Select value={dept} onValueChange={(v) => resetTo(() => setDept(v))}>
            <SelectTrigger className="h-9 w-40 text-sm">
              <SelectValue placeholder="부서" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>부서 전체</SelectItem>
              {departments!.map((d) => (
                <SelectItem key={d.id} value={d.id}>
                  {d.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <Select value={status} onValueChange={(v) => resetTo(() => setStatus(v as StatusFilter))}>
          <SelectTrigger className="h-9 w-32 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">재직</SelectItem>
            <SelectItem value="inactive">퇴사</SelectItem>
            <SelectItem value="all">전체</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading && <LoadingState />}
      {error && <InfoBox tone="danger">{(error as Error).message}</InfoBox>}

      {data && data.employees.length === 0 && (
        <div className="rounded-card bg-card">
          <EmptyState title="조건에 맞는 직원이 없습니다." icon="search" />
        </div>
      )}

      {data && data.employees.length > 0 && (
        <>
          <ul className="divide-y divide-slate-100 overflow-hidden rounded-card bg-card">
            {data.employees.map((e) => (
              <li key={e.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {/*
                      행 전체를 누르게 하지 않는다 — 안에 임시비번·퇴사 버튼이 있어
                      실수로 상세로 튀거나 반대로 버튼이 안 눌린다. 이름만 링크로 둔다.
                    */}
                    <Link to={`/admin/employees/${e.id}`} className="font-medium hover:text-primary hover:underline">
                      {e.name}
                    </Link>
                    {e.employeeNumber && <span className="text-xs text-slate-400">{e.employeeNumber}</span>}
                    {e.role !== 'EMPLOYEE' && (
                      <Badge variant={e.role === 'ADMIN' ? 'purple' : 'info'}>{ROLE_LABEL[e.role]}</Badge>
                    )}
                    {!e.isActive && <Badge variant="default">퇴사</Badge>}
                    {/* 아직 첫 로그인을 안 한 사람 — 초기 비밀번호가 그대로다 */}
                    {e.mustChangePassword && e.isActive && <Badge variant="warning">초기 비번</Badge>}
                  </div>
                  <p className="mt-0.5 text-xs text-slate-400">
                    {e.department?.name ?? '부서 미지정'}
                    {e.position?.name ? ` · ${e.position.name}` : ''}
                    {e.email ? ` · ${e.email}` : ''}
                  </p>
                </div>

                <div className="flex shrink-0 items-center gap-1.5">
                  {/* 임시 비밀번호·퇴사·복구는 전부 ADMIN 만. 부서장에게 열면 대리출석 체인이 열린다 */}
                  {isAdmin &&
                    (e.isActive ? (
                      <>
                        <Button
                          size="sm"
                          variant="secondary"
                          loading={issue.isPending && issue.variables?.id === e.id}
                          onClick={() => issue.mutate(e)}
                        >
                          임시 비밀번호
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          loading={leave.isPending && leave.variables?.id === e.id}
                          onClick={() => setConfirmLeave(e)}
                        >
                          퇴사
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="sm"
                        variant="secondary"
                        loading={back.isPending && back.variables?.id === e.id}
                        onClick={() => setConfirmBack(e)}
                      >
                        복구
                      </Button>
                    ))}

                  {/*
                    상세로 들어가는 길. 이름 글자만 링크라(:219) **써 보기 전에는 보이지 않는다** —
                    마우스를 올리면 밑줄이 생기지만 폰에는 그것도 없다. 9/7 에 기기 화면을 못 찾았다.
                    행 전체를 링크로 두지 않는 이유는 위 주석 그대로다(버튼과 부딪힌다).
                    그래서 오른쪽 끝에 화살표만 둔다 — 버튼 옆이라 실수로 눌러도 상세로만 간다.
                  */}
                  <Link
                    to={`/admin/employees/${e.id}`}
                    aria-label={`${e.name} 상세`}
                    className="rounded-md p-1.5 text-slate-300 transition-colors hover:bg-slate-100 hover:text-slate-600"
                  >
                    <ChevronRight className="h-4 w-4" aria-hidden />
                  </Link>
                </div>
              </li>
            ))}
          </ul>

          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </>
      )}

      <AlertDialog
        open={tempPassword !== null}
        onOpenChange={(o) => !o && setTempPassword(null)}
        title="임시 비밀번호"
        message={
          tempPassword
            ? `${tempPassword.name} 님\n\n${tempPassword.password}\n\n본인에게 직접 전달하세요. 이 창을 닫으면 다시 볼 수 없습니다.`
            : ''
        }
      />

      <EmployeeCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        departments={departments ?? []}
        loginType={org?.loginType}
        onCreated={(e) => notify(`${e.name} 님을 추가했습니다`)}
      />

      <EmployeeBulkDialog open={bulkOpen} onOpenChange={setBulkOpen} />

      <ConfirmDialog
        open={confirmLeave !== null}
        onOpenChange={(o) => !o && setConfirmLeave(null)}
        title="퇴사 처리할까요?"
        message={
          confirmLeave
            ? `${confirmLeave.name} 님이 로그인할 수 없게 되고 푸시도 꺼집니다.\n지금까지의 출퇴근 기록은 그대로 남습니다.\n\n실수했다면 목록에서 '복구'로 되돌릴 수 있습니다.`
            : ''
        }
        confirmText="퇴사 처리"
        /* 되돌릴 수는 있지만(복구), 누르는 즉시 그 사람은 로그인도 태깅도 못 한다 */
        destructive
        loading={leave.isPending}
        onConfirm={() => confirmLeave && leave.mutate(confirmLeave)}
      />

      <ConfirmDialog
        open={confirmBack !== null}
        onOpenChange={(o) => !o && setConfirmBack(null)}
        title="복구할까요?"
        message={
          confirmBack
            ? `${confirmBack.name} 님이 다시 로그인하고 출퇴근을 찍을 수 있게 됩니다.`
            : ''
        }
        confirmText="복구"
        loading={back.isPending}
        onConfirm={() => confirmBack && back.mutate(confirmBack)}
      />

      {toast && (
        <Toast
          message={toast}
          variant={toastError ? 'error' : 'success'}
          onClose={() => setToast('')}
        />
      )}
    </div>
  )
}
